import { useCallback, useEffect, useMemo, useState } from 'react'

import { COL_SCHEDULE_SLOTS, COL_SHIFTS } from '../../config/collections'
import { PERMISSION_KEYS } from '../../config/permissions'
import { machineTypeLabel } from '../../lib/machines'
import { pb } from '../../pocketbase'
import { useAuth } from '../../context/AuthContext'
import { ExportCsvButton } from '../../components/ExportCsvButton'
import type { CsvColumn } from '../../lib/exportCsv'
import { pocketbaseErrorMessage } from '../../lib/pocketbaseErrorMessage'
import {
  deviationPercent,
  formatDateTimeRu,
  formatDeviation,
  formatQty,
  SCHEDULE_SLOT_STATUS_LABELS,
} from '../../lib/scheduleCalculations'
import type { ScheduleSlotRecord, ShiftRecord } from '../../types/records'
import { Alert } from '../../components/ui'
import {
  btn,
  cn,
  inputClass,
  loadingClass,
  tableCellClass,
  tableClass,
  tableHeadClass,
  tableLastRowClass,
  tableRowHoverClass,
  tableWrapClass,
  toolbarClass,
} from '../../lib/ui'

function dateInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function OutputReportPage() {
  const { user, can } = useAuth()
  const canAllShifts = can(PERMISSION_KEYS.PRODUCTION_SCHEDULE_ALL_SHIFTS)

  const [fromDate, setFromDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() - 7)
    return dateInputValue(d)
  })
  const [toDate, setToDate] = useState(() => dateInputValue(new Date()))
  const [shiftId, setShiftId] = useState(() =>
    canAllShifts ? '' : (typeof user?.assignedShift === 'string' ? user.assignedShift : ''),
  )
  const [shifts, setShifts] = useState<ShiftRecord[]>([])
  const [slots, setSlots] = useState<ScheduleSlotRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')

  const load = useCallback(async () => {
    setErr('')
    setLoading(true)
    try {
      let filter = `plannedStart >= "${fromDate} 00:00:00" && plannedStart <= "${toDate} 23:59:59" && status != "cancelled"`
      if (shiftId) filter += ` && shift="${shiftId}"`

      const [sList, slotList] = await Promise.all([
        pb.collection(COL_SHIFTS).getFullList<ShiftRecord>({ sort: 'sort' }),
        pb.collection(COL_SCHEDULE_SLOTS).getFullList<ScheduleSlotRecord>({
          filter,
          expand: 'requestLine.product,machine,shift',
          sort: 'plannedStart',
        }),
      ])
      setShifts(sList.filter((s) => s.active))
      setSlots(slotList)
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка загрузки'))
      setSlots([])
    } finally {
      setLoading(false)
    }
  }, [fromDate, toDate, shiftId])

  useEffect(() => {
    void load()
  }, [load])

  const totals = useMemo(() => {
    let planned = 0
    let actual = 0
    for (const s of slots) {
      planned += Number(s.plannedQuantity) || 0
      if (s.actualQuantity != null) actual += Number(s.actualQuantity) || 0
    }
    return { planned, actual, deviation: actual - planned }
  }, [slots])

  const outputCsvColumns: CsvColumn<ScheduleSlotRecord>[] = useMemo(
    () => [
      { header: 'Дата', value: (s) => formatDateTimeRu(s.plannedStart) },
      {
        header: 'Станок',
        value: (s) => {
          const m = s.expand?.machine
          return m ? `${machineTypeLabel(m.type)} №${m.number}` : ''
        },
      },
      { header: 'Продукция', value: (s) => s.expand?.requestLine?.expand?.product?.name ?? '' },
      { header: 'Смена', value: (s) => s.expand?.shift?.name ?? '' },
      { header: 'План', value: (s) => Number(s.plannedQuantity) || 0 },
      {
        header: 'Факт',
        value: (s) => (s.actualQuantity != null ? Number(s.actualQuantity) : ''),
      },
      {
        header: 'Δ',
        value: (s) => {
          const planned = Number(s.plannedQuantity) || 0
          const actual = s.actualQuantity != null ? Number(s.actualQuantity) : null
          return formatDeviation(planned, actual)
        },
      },
      {
        header: 'Δ %',
        value: (s) => {
          const planned = Number(s.plannedQuantity) || 0
          const actual = s.actualQuantity != null ? Number(s.actualQuantity) : null
          const pct = deviationPercent(planned, actual)
          return pct != null ? `${pct > 0 ? '+' : ''}${pct}%` : ''
        },
      },
      { header: 'Статус', value: (s) => SCHEDULE_SLOT_STATUS_LABELS[s.status] ?? s.status },
    ],
    [],
  )

  if (loading && slots.length === 0) {
    return <div className={loadingClass}>Загрузка отчёта…</div>
  }

  return (
    <div>
      <p className="mb-4 text-sm text-su-text">
        Выработка по слотам графика: план, факт и отклонение за выбранный период.
      </p>
      {err && <Alert kind="err">{err}</Alert>}

      <div className={toolbarClass}>
        <label className="flex items-center gap-2 text-sm">
          <span className="text-su-text">С</span>
          <input type="date" className={inputClass} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <span className="text-su-text">По</span>
          <input type="date" className={inputClass} value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <span className="text-su-text">Смена</span>
          <select
            className={cn(inputClass, 'min-w-36')}
            value={shiftId}
            onChange={(e) => setShiftId(e.target.value)}
            disabled={!canAllShifts && !!user?.assignedShift}
          >
            {canAllShifts && <option value="">Все</option>}
            {shifts.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className={btn()} onClick={() => void load()} disabled={loading}>
          Обновить
        </button>
        {slots.length > 0 && (
          <ExportCsvButton filename={`output-report-${fromDate}-${toDate}`} rows={slots} columns={outputCsvColumns} />
        )}
      </div>

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <div className="rounded-su border border-su-border bg-su-surface p-4 shadow-su-sm">
          <p className="text-xs text-su-text">План</p>
          <p className="text-xl font-bold text-su-text-strong">{formatQty(totals.planned)}</p>
        </div>
        <div className="rounded-su border border-su-border bg-su-surface p-4 shadow-su-sm">
          <p className="text-xs text-su-text">Факт</p>
          <p className="text-xl font-bold text-su-text-strong">{formatQty(totals.actual)}</p>
        </div>
        <div className="rounded-su border border-su-border bg-su-surface p-4 shadow-su-sm">
          <p className="text-xs text-su-text">Отклонение</p>
          <p
            className={cn(
              'text-xl font-bold',
              totals.deviation < 0 ? 'text-red-600' : totals.deviation > 0 ? 'text-emerald-600' : 'text-su-text-strong',
            )}
          >
            {formatDeviation(totals.planned, totals.actual)}
          </p>
        </div>
      </div>

      <div className={cn(tableWrapClass, tableLastRowClass)}>
        <table className={tableClass}>
          <thead>
            <tr>
              <th className={tableHeadClass}>Дата</th>
              <th className={tableHeadClass}>Станок</th>
              <th className={tableHeadClass}>Продукция</th>
              <th className={tableHeadClass}>Смена</th>
              <th className={tableHeadClass}>План</th>
              <th className={tableHeadClass}>Факт</th>
              <th className={tableHeadClass}>Δ</th>
              <th className={tableHeadClass}>Δ %</th>
              <th className={tableHeadClass}>Статус</th>
            </tr>
          </thead>
          <tbody>
            {slots.length === 0 && (
              <tr className={tableRowHoverClass}>
                <td className={tableCellClass} colSpan={9}>
                  <em>Нет слотов за период</em>
                </td>
              </tr>
            )}
            {slots.map((s) => {
              const planned = Number(s.plannedQuantity) || 0
              const actual = s.actualQuantity != null ? Number(s.actualQuantity) : null
              const pct = deviationPercent(planned, actual)
              const machine = s.expand?.machine
              return (
                <tr key={s.id} className={tableRowHoverClass}>
                  <td className={tableCellClass}>{formatDateTimeRu(s.plannedStart)}</td>
                  <td className={tableCellClass}>
                    {machine ? `${machineTypeLabel(machine.type)} №${machine.number}` : '—'}
                  </td>
                  <td className={tableCellClass}>{s.expand?.requestLine?.expand?.product?.name ?? '—'}</td>
                  <td className={tableCellClass}>{s.expand?.shift?.name ?? '—'}</td>
                  <td className={tableCellClass}>{formatQty(planned)}</td>
                  <td className={tableCellClass}>{actual != null ? formatQty(actual) : '—'}</td>
                  <td className={tableCellClass}>{formatDeviation(planned, actual)}</td>
                  <td className={tableCellClass}>{pct != null ? `${pct > 0 ? '+' : ''}${pct}%` : '—'}</td>
                  <td className={tableCellClass}>{SCHEDULE_SLOT_STATUS_LABELS[s.status] ?? s.status}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-xs text-su-text print:hidden">
        Для печати сменного рапорта используйте печать страницы браузера (Ctrl+P).
      </p>
    </div>
  )
}
